import React, { useEffect, useState } from "react";
import { TableRow, TableCell, Stack, Typography } from "@mui/material";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedMonthData,
  setSelectedMonthForecastContentSharedUid,
  setSelectedSite,
} from "../../../../../../features/forecastersSlice";
import MetricsByMonthDialogue from "../../ForeCasterInfo/MetricsByMonth/MetricsByMonth";
import { setUserToken } from "../../../../../../features/userSlice";
import { usePastForecast } from "../../../../../../customHooks/usePastForecast";
import NonEditableForecastSkeleton from "./NonEditableForecastSkeleton";
import {
  formatPercentageNonRound,
  formatPercentageNonRoundUptoTwo,
} from "../../../../../../utils/helpers";

interface Props {
  showOnlyForecasts: any;
  NonEditableMonths: any;
  RequiredPublishedMonthData: any;
  isMonthPassed: any;
  formatNumber: any;
  MainLoading: any;
  computedLeftOffsets: any;
  computedRightOffsets: any;
}

export const NonEditableProfit = ({
  showOnlyForecasts,
  NonEditableMonths,
  RequiredPublishedMonthData,
  isMonthPassed,
  formatNumber,
  MainLoading,
  computedLeftOffsets,
  computedRightOffsets,
}: Props) => {
  const token = useSelector(setUserToken);
  const oauthTokenHeader = token?.payload?.user?.userToken;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const selectedSiteFromRedux = useSelector(
    (state: any) => state.forecasters?.selectedSite
  );
  const selectedSiteFromURL = searchParams.get("selectedSite");
  const selectedYear = searchParams.get("selectedYear") as any;
  const selectedSite = selectedSiteFromRedux || selectedSiteFromURL;

  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    if (
      location.pathname === "/assets/overview/forecaster/forecasterInfo" &&
      newSearchParams.get("page") !== "0"
    ) {
      newSearchParams.set("page", "0");
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, location.pathname, navigate]);

  // Ensure selectedSite is set in Redux store when component mounts
  useEffect(() => {
    if (selectedSite && !selectedSiteFromRedux) {
      dispatch(setSelectedSite(selectedSite));
    }
  }, [selectedSite, selectedSiteFromRedux, dispatch]);

  const [openMetrics, setOpenMetrics] = useState(false);

  const handleClose = () => {
    setOpenMetrics(false);
  };

  const PassedData =
    RequiredPublishedMonthData === undefined
      ? NonEditableMonths
      : RequiredPublishedMonthData;

  const handleInfoIconClick = (row: any) => {
    dispatch(setSelectedMonthData(row));
    dispatch(setSelectedMonthForecastContentSharedUid(row?.content_shared_uid));
  };

  const { forecastData, loading } = usePastForecast(
    PassedData,
    oauthTokenHeader,
    selectedSite
  );

  const CleanedHistoryData = forecastData.map((data: any) => {
    const cleanedItems = Object.fromEntries(
      Object.entries(data).filter(
        ([key]) =>
          !["input_params", "meta_data", "output_metadata"].includes(key)
      )
    );
    return cleanedItems;
  });

  const getDeviation = (month: string, metricName: string) => {
    // 🧠 Check if the month has passed — only then include it
    if (!isMonthPassed(month)) return null;

    // ✅ Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData.filter((item: any) =>
      isMonthPassed(item.month)
    ).find((item: any) => item.month === month);

    if (!monthData) return null;

    const metricItem = monthData.query_output.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase()
    );

    return metricItem?.Deviation ?? null;
  };

  const getActual = (month: string, metricName: string) => {
    // Check if the month has passed — only then include it
    if (!isMonthPassed(month)) return null;

    // Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData.filter((item: any) =>
      isMonthPassed(item.month)
    ).find((item: any) => item.month === month);

    if (!monthData) return null;

    const metricItem = monthData.query_output.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase()
    );

    return metricItem?.Actual ?? null;
  };

  const hasRequiredData =
    RequiredPublishedMonthData !== undefined &&
    RequiredPublishedMonthData.length > 0;
  const isLoading =
    RequiredPublishedMonthData !== undefined
      ? loading || MainLoading || (hasRequiredData && forecastData.length === 0)
      : MainLoading;

  return (
    <>
      {isLoading ? (
        <NonEditableForecastSkeleton />
      ) : (
        <>
          {PassedData?.map((row: any, rowIndex: any) => (
            <>
              <React.Fragment key={rowIndex}>
                <TableRow
                  sx={{
                    backgroundColor: isMonthPassed(row.month)
                      ? "#F5F5F5"
                      : "#FFFFFF",
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                    },
                    height: 35,
                  }}
                >
                  <TableCell
                    align="left"
                    sx={{
                      backgroundColor: "#ffffff",
                      padding: "0px 8px !important",
                      position: "sticky",
                      left: computedLeftOffsets[0] ?? 0,
                      zIndex: 2,
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      {RequiredPublishedMonthData !== undefined ||
                      RequiredPublishedMonthData?.length > 0 ? (
                        <RouterLink
                          to={`MonthlySummery?${(() => {
                            const params = new URLSearchParams(searchParams);
                            params.set("page", "0");
                            params.set(
                              "contentSharedUid",
                              row?.content_shared_uid
                            );
                            params.set(
                              "month",
                              `${row?.displayMonth} ${selectedYear}`
                            );
                            params.set("selectedMonth", row?.month);
                            return params.toString();
                          })()}`}
                          onClick={() => handleInfoIconClick(row)}
                          style={{
                            textDecoration: "none",
                            color: "inherit",
                            display: "flex",
                            gap: "4px",
                          }}
                        >
                          <Typography variant="body2" color="secondary">
                            ({showOnlyForecasts ? "F" : "A"})
                          </Typography>
                          {row?.displayMonth ? (
                            <Typography
                              variant="body2"
                              color="secondary"
                              component="span"
                              sx={{ textDecoration: "underline" }}
                            >
                              {row.displayMonth}
                            </Typography>
                          ) : (
                            ""
                          )}
                        </RouterLink>
                      ) : (
                        <>
                          <Typography variant="body2" color="secondary">
                            (A)
                          </Typography>
                          {row?.displayMonth ? (
                            <Typography
                              variant="body2"
                              color="secondary"
                              component="span"
                            >
                              {row.displayMonth}
                            </Typography>
                          ) : (
                            ""
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#ffffff",
                      padding: "0px 8px !important",
                      position: "sticky",
                      left: computedLeftOffsets[1],
                      zIndex: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.nrpu ? (
                              `${formatNumber(parseFloat(row.nrpu))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  parseFloat(getActual(row.month, "nrpu"))
                                )}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "nrpu") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.nrpu ? (
                          `${Math.round(Number(row.nrpu))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      padding: "0px 8px !important",
                      position: "sticky",
                      left: computedLeftOffsets[2],
                      zIndex: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.ftd ? (
                              `${formatNumber(parseFloat(row.ftd))}`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "ftd")) || 0}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(getDeviation(row.month, "ftd") * 100).toFixed(
                                  1
                                )}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.ftd ? (
                          `${formatNumber(parseFloat(row.ftd))}`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#ffffff",
                      padding: "0px 8px !important",
                      position: "sticky",
                      left: computedLeftOffsets[3],
                      zIndex: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.avg_net_rev_per_active_user ? (
                              `${Number(
                                row.avg_net_rev_per_active_user
                              ).toFixed(2)}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {`${Number(
                                  getActual(
                                    row.month,
                                    "avg_net_rev_per_active_user"
                                  )
                                ).toFixed(2)}$`}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(
                                    row.month,
                                    "avg_net_rev_per_active_user"
                                  ) * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.avg_net_rev_per_active_user ? (
                          `${Number(row.avg_net_rev_per_active_user).toFixed(
                            2
                          )}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#ffffff",
                      padding: "0px 8px !important",
                      position: "sticky",
                      left: computedLeftOffsets[4],
                      zIndex: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.cpa_pv ? (
                              `${formatNumber(parseFloat(row.cpa_pv))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "cpa_pv")) ||
                                  0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "cpa_pv") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.cpa_pv ? (
                          `${formatNumber(parseFloat(row.cpa_pv))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#ffffff",
                      padding: "0px 8px !important",
                      position: "sticky",
                      left: computedLeftOffsets[5],
                      zIndex: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.cvr ? (
                              `${formatPercentageNonRound(row?.cvr)}`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatPercentageNonRound(
                                  getActual(row.month, "CVR")
                                )}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(getDeviation(row.month, "CVR") * 100).toFixed(
                                  1
                                )}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.cvr ? (
                          `${formatPercentageNonRound(row?.cvr)}`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      padding: "0px 8px !important",
                      position: "sticky",
                      left: computedLeftOffsets[6],
                      zIndex: 2,
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.cac ? (
                              `${formatNumber(parseFloat(row.cac))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "CAC")) || 0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(getDeviation(row.month, "CAC") * 100).toFixed(
                                  1
                                )}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.cac ? (
                          `${formatNumber(parseFloat(row.cac))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.active_users ? (
                              `${formatNumber(parseFloat(row.active_users))}`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  getActual(row.month, "active_users")
                                ) || 0}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "active_users") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.active_users ? (
                          `${formatNumber(parseFloat(row.active_users))}`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.old_active_users ? (
                              `${formatNumber(
                                parseFloat(row.old_active_users)
                              )}`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  getActual(row.month, "old_active_users")
                                ) || 0}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "old_active_users") *
                                  100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.old_active_users ? (
                          `${formatNumber(parseFloat(row.old_active_users))}`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.acquired_active_users ? (
                              `${formatNumber(
                                parseFloat(row.acquired_active_users)
                              )}`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  getActual(row.month, "acquired_active_users")
                                ) || 0}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(
                                    row.month,
                                    "acquired_active_users"
                                  ) * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.acquired_active_users ? (
                          `${formatNumber(
                            parseFloat(row.acquired_active_users)
                          )}`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.growth_target ? (
                              `${formatPercentageNonRoundUptoTwo(
                                row?.growth_target
                              )}`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatPercentageNonRoundUptoTwo(
                                  getActual(row.month, "growth")
                                )}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "growth") * 100
                                ).toFixed(2)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.growth_target ? (
                          `${formatPercentageNonRoundUptoTwo(
                            row?.growth_target
                          )}`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.net_revenue ? (
                              `${formatNumber(parseFloat(row.net_revenue))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  getActual(row.month, "net_rev")
                                ) || 0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "net_rev") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.net_revenue ? (
                          `${formatNumber(parseFloat(row.net_revenue))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.rs ? (
                              `${formatNumber(parseFloat(row.rs))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  getActual(row.month, "rs_income_dollar")
                                ) || 0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "rs_income_dollar") *
                                  100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.rs ? (
                          `${formatNumber(parseFloat(row.rs))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.rs_pv ? (
                              `${formatNumber(parseFloat(row.rs_pv))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "rs_pv")) ||
                                  0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "rs_pv") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.rs_pv ? (
                          `${formatNumber(parseFloat(row.rs_pv))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.cpa ? (
                              `${formatNumber(parseFloat(row.cpa))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  getActual(row.month, "cpa_income_dollar")
                                ) || 0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "cpa_income_dollar") *
                                  100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.cpa ? (
                          `${formatNumber(parseFloat(row.cpa))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.total_income ? (
                              `${formatNumber(parseFloat(row.total_income))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  getActual(row.month, "total_income")
                                ) || 0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "total_income") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.total_income ? (
                          `${formatNumber(parseFloat(row.total_income))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.clicks ? (
                              `${formatNumber(parseFloat(row.clicks))}`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "clicks")) ||
                                  0}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "clicks") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.clicks ? (
                          `${formatNumber(parseFloat(row.clicks))}`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.epc_cpa ? (
                              `${formatNumber(parseFloat(row.epc_cpa))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(
                                  getActual(row.month, "EPC_CPA")
                                ) || 0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "EPC_CPA") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.epc_cpa ? (
                          `${formatNumber(parseFloat(row.epc_cpa))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.epc_rs ? (
                              `${formatNumber(parseFloat(row.epc_rs))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "EPC_RS")) ||
                                  0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "EPC_RS") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.epc_rs ? (
                          `${formatNumber(parseFloat(row.epc_rs))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.epc ? (
                              `${formatNumber(parseFloat(row.epc))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "EPC")) || 0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(getDeviation(row.month, "EPC") * 100).toFixed(
                                  1
                                )}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.epc ? (
                          `${formatNumber(parseFloat(row.epc))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.cpc ? (
                              `${formatNumber(parseFloat(row.cpc))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "CPC")) || 0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(getDeviation(row.month, "CPC") * 100).toFixed(
                                  1
                                )}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.cpc ? (
                          `${formatNumber(parseFloat(row.cpc))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.profit ? (
                              `${formatNumber(parseFloat(row.profit))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "profit")) ||
                                  0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "profit") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.profit ? (
                          `${formatNumber(parseFloat(row.profit))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      padding: "0px 8px !important",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            row?.spend ? (
                              `${formatNumber(parseFloat(row.spend))}$`
                            ) : (
                              ""
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatNumber(getActual(row.month, "spend")) ||
                                  0}
                                $
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "spend") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : row?.spend ? (
                          `${formatNumber(parseFloat(row.spend))}$`
                        ) : (
                          ""
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: isMonthPassed(row.month)
                        ? "#F5F5F5"
                        : "#FFFFFF",
                      padding: "0px 8px !important",
                      position: "sticky",
                      right: computedRightOffsets[23],
                      zIndex: 2,
                      // borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            formatPercentageNonRound(row?.avg_rs_deal_p)
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatPercentageNonRound(
                                  getActual(row.month, "avg_rs_p")
                                )}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "avg_rs_p") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : (
                          formatPercentageNonRound(row?.avg_rs_deal_p)
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: isMonthPassed(row.month)
                        ? "#F5F5F5"
                        : "#FFFFFF",
                      padding: "0px 8px !important",
                      position: "sticky",
                      right: computedRightOffsets[24],
                      zIndex: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            formatPercentageNonRound(row?.churn_rate)
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatPercentageNonRound(
                                  getActual(row.month, "churn_rate")
                                )}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "churn_rate") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : (
                          formatPercentageNonRound(row?.churn_rate)
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: isMonthPassed(row.month)
                        ? "#F5F5F5"
                        : "#FFFFFF",
                      padding: "0px 8px !important",
                      position: "sticky",
                      right: computedRightOffsets[25],
                      zIndex: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            formatPercentageNonRound(
                              row?.br_to_ng_active_rs_ratio
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatPercentageNonRound(
                                  getActual(row.month, "rs_br_ng")
                                )}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(row.month, "rs_br_ng") * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : (
                          formatPercentageNonRound(
                            row?.br_to_ng_active_rs_ratio
                          )
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    data-tracker="131"
                    sx={{
                      backgroundColor: isMonthPassed(row.month)
                        ? "#F5F5F5"
                        : "#FFFFFF",
                      padding: "0px 8px !important",
                      position: "sticky",
                      right: computedRightOffsets[26],
                      zIndex: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {RequiredPublishedMonthData !== undefined ||
                        RequiredPublishedMonthData?.length > 0 ? (
                          showOnlyForecasts ? (
                            formatPercentageNonRound(
                              row?.ratio_between_acquired_to_ftd
                            )
                          ) : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2">
                                {formatPercentageNonRound(
                                  getActual(
                                    row.month,
                                    "Ratio_between_acquired_to_ftd"
                                  )
                                )}
                              </Typography>
                              <Typography variant="body2" color="#00000099">
                                (
                                {(
                                  getDeviation(
                                    row.month,
                                    "Ratio_between_acquired_to_ftd"
                                  ) * 100
                                ).toFixed(1)}
                                %)
                              </Typography>
                            </Stack>
                          )
                        ) : (
                          formatPercentageNonRound(
                            row?.ratio_between_acquired_to_ftd
                          )
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              </React.Fragment>
              <MetricsByMonthDialogue
                open={openMetrics}
                handleClose={handleClose}
                selectedSiteFromParent={selectedSite}
              />
            </>
          ))}
        </>
      )}
    </>
  );
};
